import { Component, OnInit, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { Router } from '@angular/router';
import { Validators, UntypedFormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import * as fromApp from './../../store/app.reducer';
import * as AuthActions from './../store/auth.actions';
import { customEmailValidator } from '../../shared/validators/email.validator';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ToasterService } from '../../service/toaster.service';
import { MsalService } from '@azure/msal-angular';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthenticationResult } from '@azure/msal-browser';
import { EmployeeModel } from '../../models/employee.model';
import { CustomerModel } from '../../models/customer.model';

declare const google: any;

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, AfterViewInit {
    @ViewChild('loginVideo') loginVideo!: ElementRef<HTMLVideoElement>;
    @ViewChild('googleSignInButton') googleSignInButton!: ElementRef;
    public isVideoLoaded: boolean = false;
    public showPasswords: boolean = false;
    public formSubmitted: boolean = false;
    public msalInitialized: boolean = false;
    public isLoading: boolean = false;
    public lastUsedAccount: any = null;

    public loginForm = this.formBuilder.group({
        email: ['', [Validators.required, customEmailValidator()]],
        password: ['', [Validators.required]],
    });

    constructor(
        private router: Router,
        private formBuilder: UntypedFormBuilder,
        private translate: TranslateService,
        private store: Store<fromApp.AppState>,
        private http: HttpClient,
        private toasterService: ToasterService,
        private msalService: MsalService
    ) { }

    async ngOnInit(): Promise<void> {
        const savedUser = sessionStorage.getItem("lastGoogleUser");
        if (savedUser) {
            this.lastUsedAccount = JSON.parse(savedUser);
        }
        const videoUrls = ['assets/gifs/video-webm.webm', 'assets/gifs/video.mp4'];
        videoUrls.forEach(videoUrl => {
            fetch(videoUrl, { method: 'GET', cache: 'force-cache' });
        });
        this.setupFormValidation();
        try {
            sessionStorage.clear();
            await this.msalService.instance.initialize();
            this.msalInitialized = true;
        } catch (error) {
            console.error('Failed to initialize MSAL during component initialization', error);
        }
    }

    ngAfterViewInit(): void {
        if (typeof google !== 'undefined' && google?.accounts?.id) {
            google.accounts.id.initialize({
                client_id: environment.googleClientId,
                callback: this.handleGoogleSignIn.bind(this),
                cancel_on_tap_outside: true,
                ux_mode: 'popup',
                auto_select: false
            });

            google.accounts.id.prompt((notification: any) => {
                const isNotDisplayed = notification.isNotDisplayed?.() || notification.getNotDisplayedReason?.();
                const isSkipped = notification.isSkippedMoment?.() || notification.getSkippedReason?.();

                if (isNotDisplayed || isSkipped) {
                    console.log("One Tap not displayed - user may have dismissed it or no account available");
                }
            });
        }

        const video = this.loginVideo.nativeElement;
        video.muted = true; // required for autoplay
        video.play().catch(err => {
            console.warn('Autoplay blocked:', err);
        });
    }

    get email() {
        return this.loginForm.get('email');
    }

    get password() {
        return this.loginForm.get('password');
    }

    private setupFormValidation(): void {
        this.loginForm.valueChanges.subscribe(() => {
            if (this.formSubmitted) {
                this.formSubmitted = false;
            }
            if (this.formSubmitted) {
                this.validateForm();
            }
        });
    }

    private validateForm(): void {
        if (this.loginForm.valid) {
            this.formSubmitted = true;
            return;
        }
    }

    public shouldShowError(controlName: string): boolean {
        const control = this.loginForm.get(controlName);
        return Boolean((control?.touched || this.formSubmitted) && control?.errors);
    }

    public getErrorMessage(controlName: string): string {
        const control = this.loginForm.get(controlName);
        if (!control || !control.errors) return '';

        const errors = control.errors;
        const controlNameCapitalized = controlName.charAt(0).toUpperCase() + controlName.slice(1);

        if (errors['required']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.required')}`;
        }
        if (errors['email']) {
            return this.translate.instant('form.validation.email');
        }
        if (errors['minlength']) {
            return `${controlNameCapitalized} ${this.translate.instant('form.validation.min-length', { minLength: errors['minlength'].requiredLength })}`;
        }
        if (errors['passwordComplexity']) {
            const missing = errors['passwordComplexity'].missing;
            const requirements = [];
            if (missing.upperCase) requirements.push(this.translate.instant('form.validation.uppercase-letter'));
            if (missing.lowerCase) requirements.push(this.translate.instant('form.validation.lowercase-letter'));
            if (missing.numeric) requirements.push(this.translate.instant('form.validation.number'));
            if (missing.specialChar) requirements.push(this.translate.instant('form.validation.special-character'));

            return `${controlNameCapitalized} ${this.translate.instant('form.validation.must-include')}: ${requirements.join(', ')}`;
        }

        return '';
    }

    public togglePasswordVisibility(field: 'password' | 'confirmPassword'): void {
        if (field === 'password') {
            this.showPasswords = !this.showPasswords;
        }
    }

    public getPasswordFieldType(field: 'password'): string {
        if (field === 'password') {
            return this.showPasswords ? 'text' : 'password';
        }
        return 'password';
    }

    public onSubmit(): void {
        this.formSubmitted = true;
        if (this.isLoading) return;

        this.isLoading = true;
        if (this.loginForm.valid) {
            const formData = {
                email: this.email.value,
                password: this.password.value
            };
            this.store.dispatch(new AuthActions.LoginStart(formData));
            this.isLoading = false;
        } else {
            Object.keys(this.loginForm.controls).forEach(key => {
                const control = this.loginForm.get(key);
                control?.markAsTouched();
            });
            this.isLoading = false;
        }
    }

    onVideoLoaded() {
        this.isVideoLoaded = true;
    }

    onVideoError(event: ErrorEvent) {
        console.warn('Video loading failed:', event);
        this.isVideoLoaded = false;

        const failedVideo = (event.target as HTMLVideoElement)?.currentSrc;

        if (failedVideo && 'caches' in window) {
            caches.keys().then(cacheNames => {
                cacheNames.forEach(cacheName => {
                    caches.open(cacheName).then(cache => {
                        cache.delete(failedVideo).catch(err => {
                            console.error('Failed to delete video from cache:', err);
                        });
                    });
                });
            });
        }
    }

    public signInWithGoogle(): void {
        if (typeof google === 'undefined' || !google?.accounts?.id) {
            this.toasterService.showError('Google Sign-In is not available. Please try again.');
            return;
        }

        this.isLoading = true;

        const buttonDiv = document.createElement('div');
        google.accounts.id.renderButton(buttonDiv, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            // context: 'use'
        });

        // Programmatically click the button to trigger Google sign-in
        const googleButton = buttonDiv.querySelector('.nsm7Bb-HzV7m-LgbsSe');
        if (googleButton) {
            (googleButton as HTMLElement).click();
        } else {
            google.accounts.id.prompt((notification: any) => {
                const isNotDisplayed = notification.isNotDisplayed?.() || notification.getNotDisplayedReason?.();
                const isSkipped = notification.isSkippedMoment?.() || notification.getSkippedReason?.();
                if (isNotDisplayed || isSkipped) {
                    this.isLoading = false;
                }
            });
        }

        setTimeout(() => {
            if (buttonDiv && document.body.contains(buttonDiv)) {
                document.body.removeChild(buttonDiv);
            }
            this.isLoading = false;
        }, 5000);

    }

    handleGoogleSignIn(response: any) {
        if (!response || !response.credential) {
            this.isLoading = false;
            return;
        }
        this.http.post(`${environment.apiUrl}/auth/google/callback`, { token: response.credential })
            .subscribe({
                next: (res: any) => {
                    this.isLoading = false;
                    localStorage.setItem('jwt', res.accessToken);
                    localStorage.setItem('authProvider', 'google');
                    if (res.user) {
                        localStorage.setItem('locale', res.user.locale);
                        this.translate.setDefaultLang(res.user.locale);
                        this.translate.use(res.user.locale);
                    }
                    this.store.dispatch(new AuthActions.Autologin());
                    this.store.select(state => state.auth)
                        .pipe(
                            filter(authState => !!authState.employee),
                            take(1)
                        )
                        .subscribe((authState) => {
                            this.toasterService.showSuccess('auth.login.login-success');
                            this.navigateAfterSocialLogin(authState.employee);
                        });
                },
                error: (err) => {
                    this.isLoading = false;
                    console.error('Google login failed', err);
                    this.toasterService.showError(err.error?.error || 'Authentication failed');
                }
            });
    }

    private navigateAfterSocialLogin(employee: EmployeeModel): void {
        if (employee?.customers?.length > 0) {
            this.router.navigate(['/components/assignments']);
            return;
        }
        this.createDefaultCustomerAndNavigateToAssignments(employee);
    }

    private createDefaultCustomerAndNavigateToAssignments(employee: EmployeeModel): void {
        const formData = this.buildDefaultCustomerFormData(employee);
        this.http.post<{ data: CustomerModel }>(`${environment.apiUrl}/customers`, formData).subscribe({
            next: (res) => {
                localStorage.setItem('customer', res.data.id.toString());
                this.store.dispatch(new AuthActions.CreateCustomerSuccess({ customer: res.data }));
                this.store.dispatch(new AuthActions.SwitchCompanyAccount({ customer: res.data }));
                this.router.navigate(['/components/assignments']);
            },
            error: (err) => {
                this.toasterService.showError(err.error?.error || 'Could not auto create company');
                this.router.navigate(['/components/assignments']);
            }
        });
    }

    private buildDefaultCustomerFormData(employee: EmployeeModel): FormData {
        const formData = new FormData();
        const defaultCompanyName = this.getDefaultCompanyName(employee);
        const contactPerson = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim() || defaultCompanyName;

        formData.append('idNumber', this.getGeneratedOrganisationNumber());
        formData.append('companyName', defaultCompanyName);
        formData.append('contactPerson', contactPerson);
        formData.append('email', employee?.email || '');
        formData.append('phone', employee?.phone || '');
        formData.append('address', '');
        formData.append('city', '');
        formData.append('biography', '');
        formData.append('customerWebsite', '');
        formData.append('is_auto_created_from_registration', 'true');
        return formData;
    }

    private getDefaultCompanyName(employee: EmployeeModel): string {
        const fullName = `${employee?.firstName || ''} ${employee?.lastName || ''}`.trim();
        if (fullName) {
            return fullName;
        }
        const emailPrefix = (employee?.email || '').split('@')[0]?.trim();
        return emailPrefix || 'New Company';
    }

    private getGeneratedOrganisationNumber(): string {
        const digits = Date.now().toString();
        return digits.slice(-10).padStart(10, '0');
    }

    private decodeJwt(token: string): any {
        try {
            return JSON.parse(atob(token.split('.')[1]));
        } catch (e) {
            console.error('Invalid JWT', e);
            return {};
        }
    }

    public async loginWithMicrosoft(): Promise<void> {
        if (this.isLoading) return;

        this.isLoading = true;
        try {
            const loginResult: AuthenticationResult = await this.msalService.instance.loginPopup({
                scopes: ['user.read', 'openid', 'profile', 'email'],
                prompt: 'select_account'
            });

            if (loginResult) {
                this.msalService.instance.setActiveAccount(loginResult.account);

                let tokenResult: AuthenticationResult;

                try {
                    tokenResult = await firstValueFrom(this.msalService.acquireTokenSilent({
                        scopes: ['user.read'],
                        account: loginResult.account!
                    }));
                } catch (e) {
                    tokenResult = await firstValueFrom(this.msalService.acquireTokenPopup({
                        scopes: ['user.read']
                    }));
                }

                if (!tokenResult) {
                    throw new Error('Failed to acquire token');
                }

                const response = await firstValueFrom(this.http.post(`${environment.apiUrl}/auth/microsoft/token`, {
                    token: tokenResult.accessToken
                }));

                const res: any = response;
                localStorage.setItem('jwt', res.token.accessToken);
                localStorage.setItem('authProvider', 'microsoft');
                if (res.user) {
                    localStorage.setItem('locale', res.user.locale);
                    this.translate.setDefaultLang(res.user.locale);
                    this.translate.use(res.user.locale);
                }

                this.store.dispatch(new AuthActions.Autologin());
                this.store.select(state => state.auth)
                    .pipe(
                        filter(authState => !!authState.employee),
                        take(1)
                    )
                    .subscribe((authState) => {
                        this.isLoading = false;
                        this.toasterService.showSuccess('auth.login.login-success');
                        this.navigateAfterSocialLogin(authState.employee);
                    });
            }
        } catch (err: any) {
            console.error('Microsoft login failed', err);
            this.toasterService.showError(err.message || 'Authentication failed');
        } finally {
            this.isLoading = false;
        }
    }
}